import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import '../../assets/styles/AdminShared.css';

const AdminHomePageRevision = () => {
  const { projectId } = useParams(); // Get the projectId from the route
  const [projectName, setProjectName] = useState("Loading...");
  const [responses, setResponses] = useState({});
  const [revisionLink, setRevisionLink] = useState(""); // State for the link
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Predefined questions
  const questions = [
    "Is the headline clear and attention-grabbing? Does it effectively communicate your business's purpose?",
    "Do the images or graphics align with your brand and message? Are they high quality?",
    "Is the layout visually appealing and easy to follow?",
    "Is the text easy to read and free of errors? Does it convey your key points effectively?",
    "Is the call-to-action (e.g., 'Learn More,' 'Contact Us') clear and prominently placed?",
    "Are the colors and fonts consistent with your brand identity?",
    "Is the spacing and alignment of elements consistent and professional?",
    "Does the page load quickly and function smoothly on both desktop and mobile devices?",
    "Is the layout mobile-friendly? Do all elements display correctly on smaller screens?",
    "Does the overall tone of the homepage reflect your business's values and target audience?",
    "Are any critical sections or content missing that you feel are essential for your customers?",
    "Is there any part of the homepage that confused you or felt unclear?",
    "Are the images, videos, or animations appropriately placed and not distracting?",
    "Do you feel the homepage achieves the goals you have for your website (e.g., converting visitors, providing information, building trust)?",
    "What specific changes, if any, would you like to see in the next iteration?",
    "Is there anything else you'd like to add or address that hasn't been covered?",
  ];

  useEffect(() => {
    const fetchHomePageRevisionData = async () => {
      try {
        if (!projectId) throw new Error("Project ID is missing.");

        const projectRef = doc(db, "projects", projectId);
        const projectSnap = await getDoc(projectRef);

        if (projectSnap.exists()) {
          const projectData = projectSnap.data();
          setProjectName(projectData.projectName || "Unnamed Project");
          setResponses(projectData.revisionDetails?.homePageRevision?.form || {});
          setRevisionLink(projectData.revisionDetails?.homePageRevision?.link || ""); // Fetch the link
        } else {
          throw new Error("Project not found.");
        }
      } catch (err) {
        console.error("Failed to fetch project data:", err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchHomePageRevisionData();
  }, [projectId]);

  const handleInputChange = (question, value) => {
    setResponses((prevResponses) => ({
      ...prevResponses,
      [question]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, {
        "revisionDetails.homePageRevision.form": responses,
        "revisionDetails.homePageRevision.link": revisionLink, // Save the link
      });

      alert("Responses and link updated successfully!");
    } catch (err) {
      console.error("Failed to update responses:", err.message);
      setError("Failed to update responses: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <a href={`/admin/project/${projectId}`} className="ap-back">← Back to project</a>
        <p className="ap-eyebrow">Admin — Revision</p>
        <h1 className="ap-title">{projectName}: Home Page Revision</h1>
        <p className="ap-subtitle">View or edit the client's responses for this revision.</p>
      </div>

      {loading ? (
        <div className="ap-loading"><p>Loading...</p></div>
      ) : error ? (
        <div className="ap-loading ap-loading--error"><p>{error}</p></div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="ap-section">
            <div className="ap-section-header">
              <h2 className="ap-section-title">Revision Link</h2>
            </div>
            <div className="ap-section-body">
              <div className="ap-form-group">
                <label>Revision Link</label>
                <input type="text" value={revisionLink} onChange={(e) => setRevisionLink(e.target.value)} placeholder="Enter or update the revision link" />
              </div>
            </div>
          </div>

          <div className="ap-section">
            <div className="ap-section-header">
              <h2 className="ap-section-title">Client Responses</h2>
            </div>
            <div className="ap-section-body">
              {questions.map((question, index) => (
                <div key={index} className="ap-form-group">
                  <label>{question}</label>
                  <textarea value={responses[question] || ""} onChange={(e) => handleInputChange(question, e.target.value)} placeholder="Enter response here..." />
                </div>
              ))}
            </div>
          </div>

          <div className="ap-btn-row" style={{ marginTop: '24px' }}>
            <button type="submit" className="ap-btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminHomePageRevision;
